import { Category } from '@fcom/dapi/api/models/category';
import { FinnairServiceItem } from '@fcom/dapi/api/models/finnair-service-item';
import { SubCategory } from '@fcom/dapi/api/models/sub-category';
import { CustomServiceType } from '@fcom/dapi';
import { ServiceSelections, TravelerService } from '@fcom/dapi/interfaces/services.interface';
import { SeatSelectionForFlight } from '@fcom/common/interfaces/seat-map.interface';
import { PreciseNumber } from '@fcom/core/utils/precise-number';

import {
  isBoundBasedCategory,
  isJourneyBasedCategory,
  isSeatBundle,
} from '../modules/ancillaries/utils/category.utils';

/**
 * One comparable line item, normalized from either the committed cart or the local selection state so
 * the two worlds can be diffed against each other. `key` is stable across both sides: same fragment +
 * passenger + category + subCategory + variant/seat produce the same key regardless of source.
 *
 * `unitPrice` carries the committed price when normalized from the cart and the catalog price when
 * normalized from local selections; the diff decides which one to trust per unit (see the diff step).
 */
export interface CartLineItem {
  key: string;
  /** bound id for bound-based categories, JOURNEY for journey-based, otherwise segment id — matches the
   *  fragment id the selection reducer keys local state by. */
  fragmentId: string;
  paxId: string;
  topCategory: Category;
  subCategory: SubCategory;
  variant?: string;
  seatNumber?: string;
  quantity: number;
  unitPrice: PreciseNumber;
  displayName?: string;
  /** included in the ticket type or a tier benefit — carried for display but excluded from the diff. */
  isBenefit: boolean;
}

interface LineItemKeyParts {
  fragmentId: string;
  paxId: string;
  topCategory: Category;
  subCategory: SubCategory;
  variant?: string;
  seatNumber?: string;
}

export const buildLineItemKey = ({
  fragmentId,
  paxId,
  topCategory,
  subCategory,
  variant,
  seatNumber,
}: LineItemKeyParts): string => [fragmentId, paxId, topCategory, subCategory, variant ?? seatNumber ?? ''].join('|');

const fragmentIdForCategory = (topCategory: Category, boundId: string, segmentId: string): string => {
  if (isJourneyBasedCategory(topCategory)) {
    return CustomServiceType.JOURNEY;
  }
  return isBoundBasedCategory(topCategory) ? boundId : segmentId;
};

/**
 * Inserts a line item, or sums its quantity onto an existing entry with the same key, so the returned
 * collection is canonical (one entry per key). Both normalizers share this so committed and local sides
 * stay symmetric for the diff.
 */
const addOrMergeLineItem = (byKey: Map<string, CartLineItem>, item: CartLineItem): void => {
  const existing = byKey.get(item.key);
  if (existing) {
    existing.quantity += item.quantity;
    return;
  }
  byKey.set(item.key, item);
};

const asTravelerServiceArray = (selection: TravelerService | TravelerService[] | undefined): TravelerService[] =>
  Array.isArray(selection) ? selection.filter(Boolean) : [selection].filter(Boolean);

/**
 * Flattens the committed cart's `services.unpaid` into canonical line items — one entry per key, with
 * quantities summed. The direct-flight-only invariant (bound = segment = flight) means a bound has a
 * single segment, so bound-based categories never double-count across segments.
 */
export const toCommittedLineItems = (unpaid: FinnairServiceItem[] | undefined): CartLineItem[] => {
  const byKey = new Map<string, CartLineItem>();

  (unpaid ?? []).forEach((serviceItem) => {
    const topCategory = serviceItem.category;
    serviceItem.bounds.forEach((bound) => {
      bound.segments.forEach((segment) => {
        const fragmentId = fragmentIdForCategory(topCategory, bound.id, segment.id);
        segment.passengers.forEach((passenger) => {
          passenger.services.forEach((service) => {
            const key = buildLineItemKey({
              fragmentId,
              paxId: passenger.id,
              topCategory,
              subCategory: service.subCategory,
              variant: service.variant,
              seatNumber: service.seatNumber,
            });
            addOrMergeLineItem(byKey, {
              key,
              fragmentId,
              paxId: passenger.id,
              topCategory,
              subCategory: service.subCategory,
              variant: service.variant,
              seatNumber: service.seatNumber,
              quantity: service.quantity ?? 1,
              unitPrice: PreciseNumber.fromString(service.unitPrice?.amount ?? '0'),
              displayName: service.displayName,
              isBenefit: Boolean(service.includedInTicketType || service.includedInTierBenefit),
            });
          });
        });
      });
    });
  });

  return Array.from(byKey.values());
};

/**
 * Flattens the local selection state (`ServiceSelections`) into canonical line items, mirroring
 * `toCommittedLineItems` so the two can be diffed by key. The fragment ids stored in `ServiceSelections`
 * are already the same ids the committed side keys by (bound id / segment id / JOURNEY), so keys line up
 * without re-deriving them.
 *
 * Notes:
 * - Bundle slices (incl. the non-seat combos, typed singular but stored as single-element arrays, and the
 *   seat bundles) are normalized through the array path, so cardinality doesn't matter here.
 * - Plain seat selections are NOT here: they live in the seatmap, not `ServiceSelections`, and are added by
 *   `toSeatLineItems`. Seat *bundles* do live in `ServiceSelections` and are normalized like any other slice.
 * - `unitPrice` is the catalog `pricePerItem` — the price for any not-yet-committed units.
 */
export const toLocalLineItems = (selections: ServiceSelections | undefined): CartLineItem[] => {
  const byKey = new Map<string, CartLineItem>();

  Object.entries(selections ?? {}).forEach(([category, selectionsByFragment]) => {
    const topCategory = category as Category;
    Object.entries(selectionsByFragment ?? {}).forEach(([fragmentId, selectionsByTraveler]) => {
      Object.entries(selectionsByTraveler ?? {}).forEach(([paxId, selection]) => {
        asTravelerServiceArray(selection).forEach((service) => {
          // `TravelerService.category` is misnamed — it holds the SubCategory, not the Category.
          const subCategory = service.category;
          addOrMergeLineItem(byKey, {
            key: buildLineItemKey({
              fragmentId,
              paxId,
              topCategory,
              subCategory,
              variant: service.variant,
              seatNumber: service.seatNumber,
            }),
            fragmentId,
            paxId,
            topCategory,
            subCategory,
            variant: service.variant,
            seatNumber: service.seatNumber,
            quantity: service.quantity ?? 1,
            unitPrice: PreciseNumber.fromString(service.pricePerItem?.amount ?? '0'),
            displayName: service.displayName,
            isBenefit: Boolean(service.includedInTicketType || service.includedInTierBenefit),
          });
        });
      });
    });
  });

  return Array.from(byKey.values());
};

/**
 * Builds line items for plain seat selections held in the seatmap (seats are not part of
 * `ServiceSelections`). Keys align with committed seats — which carry `seatNumber` and no `variant` — so
 * the key falls back to the seat number.
 *
 * A traveler whose flight already has a seat bundle is skipped: the bundle subsumes the seat, so emitting
 * both would double-count. `travelersWithSeatBundle` holds `${flightId}|${paxId}` keys for such travelers
 * (built by `toLocalCartLineItems`).
 */
export const toSeatLineItems = (
  seatSelectionsByFlight: Record<string, Record<string, SeatSelectionForFlight>> | undefined,
  travelersWithSeatBundle: ReadonlySet<string> = new Set()
): CartLineItem[] => {
  const byKey = new Map<string, CartLineItem>();

  Object.entries(seatSelectionsByFlight ?? {}).forEach(([flightId, selectionsByTraveler]) => {
    Object.entries(selectionsByTraveler ?? {}).forEach(([paxId, seat]) => {
      if (!seat?.seatNumber || travelersWithSeatBundle.has(`${flightId}|${paxId}`)) {
        return;
      }
      addOrMergeLineItem(byKey, {
        key: buildLineItemKey({
          fragmentId: flightId,
          paxId,
          topCategory: Category.SEAT,
          subCategory: SubCategory.SEAT,
          seatNumber: seat.seatNumber,
        }),
        fragmentId: flightId,
        paxId,
        topCategory: Category.SEAT,
        subCategory: SubCategory.SEAT,
        seatNumber: seat.seatNumber,
        quantity: 1,
        unitPrice: PreciseNumber.fromString(seat.pricePerItem?.amount ?? '0'),
        isBenefit: false,
      });
    });
  });

  return Array.from(byKey.values());
};

/**
 * The complete local line-item view: `ServiceSelections` (standalone products plus all bundles, including
 * seat bundles) unioned with plain seat selections from the seatmap, with the seat/seat-bundle precedence
 * applied so a traveler with a seat bundle doesn't also contribute a plain seat line.
 */
export const toLocalCartLineItems = (
  selections: ServiceSelections | undefined,
  seatSelectionsByFlight?: Record<string, Record<string, SeatSelectionForFlight>>
): CartLineItem[] => {
  const serviceItems = toLocalLineItems(selections);
  const travelersWithSeatBundle = new Set(
    serviceItems.filter((item) => isSeatBundle(item.topCategory)).map((item) => `${item.fragmentId}|${item.paxId}`)
  );
  return [...serviceItems, ...toSeatLineItems(seatSelectionsByFlight, travelersWithSeatBundle)];
};

/**
 * Lends the committed cart's resolved seat-type name (e.g. "Window seat") to the matching local seat line.
 * Plain seat selections live in the seatmap, which only knows the raw characteristics — the friendly label
 * is resolved by the backend and is only present on the committed cart. A committed seat shares its canonical
 * key with its local twin (the same alignment the diff relies on), so the name transfers by key. A seat that
 * isn't committed yet (a fresh or just-changed selection) has no match and keeps no display name, so the
 * shopping cart shows the generic "Selected seat" copy until the cart catches up.
 */
export const withCommittedSeatNames = (local: CartLineItem[], committed: CartLineItem[]): CartLineItem[] => {
  const committedByKey = new Map(committed.map((item) => [item.key, item]));
  return local.map((item) =>
    item.topCategory === Category.SEAT && !item.displayName
      ? { ...item, displayName: committedByKey.get(item.key)?.displayName }
      : item
  );
};
